import React, { ReactNode, useEffect, useState } from 'react';
import {
  CustomerContextProvider,
  CustomerContextInterface,
  WishlistNotLoggedInAction,
} from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { trackLoginStart } from '../hooks/useLoginTracking/useLoginTracking';
import { handleLoginCreate } from '../utils/signInOutUp/AuthenticationHelper';
import { addOrDeleteWishlist } from '../utils/wishlist';
import { ShopifyCustomerClient } from '../templates/components/customers/api/shopifyCustomer/ShopifyCustomerClient';

export const PERKS_EVENT = 'set-is-perks-event-key';

const WISHLIST_LOGIN_TRACKING_EVENT = { eventCategory: 'my account', group: 'wishlist' };

export const WISHLIST_SESSION_ITEM_KEY = 'wishlistedItemSku';

export type ShopifyCustomerProviderInterface = Pick<
  CustomerContextInterface,
  'isLoggedIn' | 'itemsInWishlist' | 'customerName' | 'addressesCount' | 'updateWishlist' | 'createAccount' | 'logIn'
>;

const shopifyCustomerClient = new ShopifyCustomerClient();

export const ShopifyCustomerProvider = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn = false, customerName = null, addressesCount = 0 } = window.Jbhifi?.myAccountLoginState || {};
  const shopifyCustomerId = window.Jbhifi?.shopifyCustomerId;
  const [isPerks, setIsPerks] = useState(Boolean(window.Jbhifi.customerDetails?.isPerks));
  const [perksJoinDate] = useState<Date | undefined>(() => {
    const perksJoinDate = window.Jbhifi.customerDetails?.perksJoinDate;
    return perksJoinDate ? new Date(perksJoinDate) : undefined;
  });

  const itemsInWishlist: Array<string> =
    window?.Jbhifi?.customerWishlist?.filter(({ Sku }) => !!Sku).map(({ Sku }) => Sku) || [];
  const wishlistedItemPreLogin = window.sessionStorage.getItem(WISHLIST_SESSION_ITEM_KEY);

  const logIn: ShopifyCustomerProviderInterface['logIn'] = (e, eventData, returnUrl) => {
    e?.stopPropagation();
    e?.preventDefault();
    trackLoginStart({ baseEventData: eventData });
    handleLoginCreate(returnUrl);
  };

  const createAccount: ShopifyCustomerProviderInterface['createAccount'] = (e, eventData, returnUrl) => {
    e?.stopPropagation();
    e?.preventDefault();
    trackLoginStart({ baseEventData: eventData, isCreate: true });
    handleLoginCreate(returnUrl, { userJourneyMode: 'LOCAL_ACCOUNT_REGISTRATION' });
  };

  const handleLoginFromUpdateWishlist = ({
    sku,
    variantId,
    notLoggedInAction,
    event,
  }: {
    event?: any;
    sku: string;
    variantId: number;
    notLoggedInAction: WishlistNotLoggedInAction;
  }): { success: boolean; error?: string } => {
    const returnUrl = `/account/login?returnUrl=${encodeURIComponent(
      window?.location?.href
    )}&wishlistItem=${encodeURIComponent(`${sku}:${variantId}`)}`;

    switch (notLoggedInAction) {
      case 'login':
        logIn(event, WISHLIST_LOGIN_TRACKING_EVENT, returnUrl);
        break;
      case 'createAccount':
        createAccount(event, WISHLIST_LOGIN_TRACKING_EVENT, returnUrl);
        break;
      case 'none':
      default:
        return { success: false, error: 'Customer not logged in.' };
    }
    return { success: false };
  };

  const updateWishlist: ShopifyCustomerProviderInterface['updateWishlist'] = async ({
    actionType,
    sku,
    variantId,
    notLoggedInAction = 'none',
    event,
  }) => {
    try {
      if (!sku) {
        throw new Error('Missing SKU');
      }

      if (!isLoggedIn) {
        return handleLoginFromUpdateWishlist({ sku, variantId, notLoggedInAction, event });
      }

      const isSuccess = await addOrDeleteWishlist(actionType, sku, variantId);
      if (isSuccess) {
        switch (actionType) {
          case 'removeItem':
            // eslint-disable-next-line react-compiler/react-compiler
            window.Jbhifi.customerWishlist = window.Jbhifi.customerWishlist.filter(item => item.Sku !== sku);
            break;
          case 'addItem':
            window.Jbhifi.customerWishlist = [
              ...window.Jbhifi.customerWishlist,
              { Sku: sku, VariantId: variantId.toString(), Added: Date.now().toString() },
            ];
            break;
          default:
        }
      }
      return {
        success: isSuccess,
        error: null,
      };
    } catch (error) {
      console.error(`Error attempting to ${actionType} item to/from wishlist`, error);
      return { success: false, error: `${error}` };
    }
  };

  const handleUpdateCustomerPreferences = async (key: string, value: string): Promise<boolean> => {
    if (!shopifyCustomerId) return false;
    const result = shopifyCustomerClient.updatePreferences({
      customerId: shopifyCustomerId,
      preferences: { [key]: value },
    });
    return (await result)?.statusCode === 200;
  };

  const handleSetIsPerks = (event: CustomEvent) => setIsPerks(event.detail.isPerks);

  useEffect(() => {
    window.addEventListener(PERKS_EVENT, handleSetIsPerks);
    return () => {
      window.removeEventListener(PERKS_EVENT, handleSetIsPerks);
    };
  }, []);

  if (isLoggedIn && wishlistedItemPreLogin) {
    if (!itemsInWishlist.includes(wishlistedItemPreLogin)) {
      itemsInWishlist.push(wishlistedItemPreLogin);
    } else {
      window.sessionStorage.removeItem(WISHLIST_SESSION_ITEM_KEY);
    }
  }

  return (
    <CustomerContextProvider
      value={{
        isLoggedIn,
        isPerks,
        birthdayMonth: window.Jbhifi.customerDetails?.birthdayMonth,
        perksJoinDate,
        customerName,
        customerPreferences: window.Jbhifi.customerPreferences,
        addressesCount,
        itemsInWishlist,
        logIn,
        createAccount,
        updateWishlist,
        handleUpdateCustomerPreferences,
      }}
    >
      {children}
    </CustomerContextProvider>
  );
};
